import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import sal from "sal.js";
import { toast } from "sonner";


import Instagram from "../Instagram/Instagram";

import { getCourses } from "@/services/courseService";
import { getErrorMessage } from "@/services/apiClient";


// HOME PAGE SECTIONS
import CoursesPageCategoryHead from "./sections/CoursesPageCategoryHead";
import { useInfiniteScrollSentinel } from "@/my_components/Common/useInfiniteScrollSentinel";
import CourseCardTwo from "./Filters/CourseCard-Two";
import InstructorsShowcase from "@/my_components/_HomePage/Home-Sections/InstructorsShowcase";
import BlogGridMinimal from "@/my_components/Blogs/Components/BlogGridMinimal";
import Newsletter_2 from "@/my_components/Newsletters/Newsletter_2";
import EventCarouse from "@/my_components/Events/EventCarouse";

// CONFIGS AND ROUTE
import { ROUTE } from "@/route/app_routes.js";
import { CONFIG } from "@/app_config.js";
import shapeImg from "../../public/images/icons/three-shape.png";
import { image_url } from "../../helper/Utilities";






const CourseRoom = () => {

    const router = useRouter();
    const category = typeof router.query.category === "string" ? router.query.category : "";
    const search = typeof router.query.search === "string" ? router.query.search : "";
    const sort = typeof router.query.sort === "string" ? router.query.sort : "";
    const skillLevel = typeof router.query.skill_level === "string" ? router.query.skill_level : "";
    // Clicking an instructor card anywhere on the platform lands here with these two params --
    // `instructor` (their tokenized tutor_id) is the actual filter sent to the backend,
    // `instructorName` is display-only, for the filter chip (see CoursesPageCategoryHead).
    const instructor = typeof router.query.instructor === "string" ? router.query.instructor : "";
    const instructorName = typeof router.query.instructorName === "string" ? router.query.instructorName : "";

    const [courses, setCourse] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        setPage(1);
    }, [category, search, sort, skillLevel, instructor]);

    useEffect(() => {
        const isFirstPage = page === 1;
        if (isFirstPage) setLoading(true);
        else setLoadingMore(true);

        getCourses({ page, category, search, sort, skill_level: skillLevel, instructor })
            .then((res) => {
                setCourse((prev) => (isFirstPage ? res.data.data : [...prev, ...res.data.data]));
                setTotalPages(res.data.last_page);
            })
            .catch((err) => toast.error(getErrorMessage(err)))
            .finally(() => {
                setLoading(false);
                setLoadingMore(false);
            });
    }, [page, category, search, sort, skillLevel, instructor]);

    const canLoadMore = !loading && !loadingMore && page < totalPages;
    const loadMore = useCallback(() => setPage((p) => p + 1), []);
    const sentinelRef = useInfiniteScrollSentinel(loadMore, canLoadMore);

    return (

        <>

            <CoursesPageCategoryHead category={category} sort={sort} skillLevel={skillLevel} instructor={instructor} instructorName={instructorName} />

            <div className="rbt-section-overlayping-top rbt-section-gapBottom">
                <div className="container">

                    {loading ? (
                        <div className="rbt-course-grid">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div className="rbt-course-card" key={i}>
                                    <div className="rbt-skel rbt-course-card__skel-media" />
                                    <div className="rbt-course-card__body">
                                        <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 10 }} />
                                        <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w90" style={{ height: 14, marginTop: 4 }} />
                                        <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 14 }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : courses.length === 0 ? (
                        <div className="rbt-course-empty">
                            <i className="feather-search"></i>
                            <h3>No courses match that yet</h3>
                            <p>Try a different search term, or clear your filters to see everything on offer.</p>
                            <Link href={ROUTE.course} className="rbt-btn btn-border radius-round">
                                Clear filters
                            </Link>
                        </div>
                    ) : (
                        <>
                            <CourseCardTwo course={courses} />

                            {loadingMore && (
                                <div className="rbt-course-grid mt--30">
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <div className="rbt-course-card" key={`more-${i}`}>
                                            <div className="rbt-skel rbt-course-card__skel-media" />
                                            <div className="rbt-course-card__body">
                                                <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 10 }} />
                                                <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w90" style={{ height: 14, marginTop: 4 }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Sentinel: an empty div the IntersectionObserver watches -- scrolling it into
                                view (with a 400px lookahead margin) triggers the next page's fetch. */}
                            {page < totalPages && <div ref={sentinelRef} aria-hidden="true" />}

                            {page >= totalPages && (
                                <p className="rbt-infinite-end">
                                    <i className="feather-check-circle"></i> You&apos;ve reached the end — that&apos;s every course matching your filters.
                                </p>
                            )}
                        </>
                    )}

                </div>
            </div>












            <div className="rbt-instructor-area bg-color-extra2 rbt-section-gap">
                <div className="container">
                    <div className="row mb--60">
                        <div className="col-lg-12 text-center">
                            <div className="section-title">
                                <span className="subtitle bg-primary-opacity">Who&apos;s Teaching</span>
                                <h2 className="title">Meet your instructors</h2>
                            </div>
                            <Link href={ROUTE.instructors} className="rbt-btn btn-border radius-round mt--20">
                                View All Instructors
                            </Link>
                        </div>
                    </div>
                    <InstructorsShowcase layout="carousel" limit={20} random />
                </div>
            </div>













        <div className="rbt-event-area rbt-section-gap bg-gradient-3">
          <div className="container">
            <div className="row mb--55">
              <div className="section-title text-center">
                <span className="subtitle bg-white-opacity">
                  INTERESTED IN JOINING?
                </span>
                <h2 className="title color-white">Upcoming Events</h2>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">
                <EventCarouse />
              </div>
            </div>
          </div>
        </div>











            <div className="rbt-rbt-blog-area rbt-section-gapTop bg-gradient-8 rbt-round-bottom-shape">
                <div className="wrapper pb--50 rbt-index-upper">
                    <div className="container">
                        <div className="row g-5 align-items-end mb--60">
                            <div className="col-lg-6 col-md-12 col-12">
                                <div className="section-title text-start">
                                <h2 className="title color-white">Latest News</h2>
                                <p className="description color-white-off mt--20">
                                    Free, structured courses built around real projects — learn skills that translate directly into a tech career.
                                </p>
                                </div>
                            </div>
                            <div className="col-lg-6 col-md-12 col-12">
                                <div className="load-more-btn text-start text-lg-end">
                                    <a className="rbt-btn btn-border icon-hover radius-round color-white-off" href={ROUTE.blog}>
                                        <span className="btn-text">See All Articles</span>
                                        <span className="btn-icon"><i className="feather-a~rrow-right"></i></span>
                                    </a>
                                </div>
                            </div>
                        </div>
                        
                        <BlogGridMinimal />
                    </div>
                </div>
            </div>






            <div className="rbt-newsletter-area bg-color-white rbt-section-gapBottom pt--60">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <Newsletter_2 />
                        </div>
                    </div>
                </div>
            </div>














        </>

    );
  
};



export default CourseRoom;
